import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
// Update calendar data of given month
import {
  TRANSITION_ACCEPT,
  TRANSITION_AUTO_ACCEPT,
  TRANSITION_COMPLETE,
  TRANSITION_DECLINE,
  TRANSITION_EXPIRE,
  TRANSITION_CANCEL,
  TRANSITION_CUSTOMER_CANCEL,
  TRANSITION_REVIEW_1_BY_CUSTOMER,
  TRANSITION_REVIEW_1_BY_PROVIDER,
  TRANSITION_REVIEW_2_BY_CUSTOMER,
  TRANSITION_REVIEW_2_BY_PROVIDER,
} from '../../util/transaction.default';
import {
  TRANSITION_CONFIRM_PAYMENT_INTENT,
  TRANSITION_MARK_PAYMENT_PENDING,
  TRANSITION_PAYMENT_PROCESSING,
  TRANSITION_PAYMENT_SUCCEEDING,
  TRANSITION_DECLINE as TRANSITION_DECLINE_DELAYED,
  TRANSITION_CANCEL_PROCESSED_PAYMENT,
  TRANSITION_CUSTOMER_CANCEL_PROCESSED_PAYMENT,
  TRANSITION_CANCEL_PAYED_PAYMENT,
  TRANSITION_CUSTOMER_CANCEL_PAYED_PAYMENT,
} from '../../util/transaction.delayed';
import moment from 'moment';

// ================ Action types ================ //

export const FETCH_TRANSACTION_REQUEST = 'app/CalendarPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/CalendarPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/CalendarPage/FETCH_TRANSACTION_ERROR';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.CalendarPage} */
const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
};

const calendarPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_TRANSACTION_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchOrdersOrSalesError: null,
      };
    case FETCH_TRANSACTION_ERROR:
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };
    default:
      return state;
  }
};

export default calendarPageReducer;

// ================ Selectors ================ //

// ================ Action creators ================ //

export const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
export const fetchTransactionSuccess = () => ({ type: FETCH_TRANSACTION_SUCCESS });
export const fetchTransactionError = (e) => ({
  type: FETCH_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchTransactionForMonth = () => async (dispatch, getState, sdk) => {
  const { fetchInProgress } = getState().CalendarPage;
  if (fetchInProgress) return;

  const allTransitions = [
    TRANSITION_ACCEPT,
    TRANSITION_AUTO_ACCEPT,
    TRANSITION_COMPLETE,
    TRANSITION_PAYMENT_SUCCEEDING,
    TRANSITION_CONFIRM_PAYMENT_INTENT,
    TRANSITION_MARK_PAYMENT_PENDING,
    TRANSITION_PAYMENT_PROCESSING,
    TRANSITION_REVIEW_1_BY_CUSTOMER,
    TRANSITION_REVIEW_1_BY_PROVIDER,
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
    // cancellations / declines — so cancelled & declined bookings appear (filtered on by the UI)
    TRANSITION_DECLINE,
    TRANSITION_DECLINE_DELAYED,
    TRANSITION_EXPIRE,
    TRANSITION_CANCEL,
    TRANSITION_CUSTOMER_CANCEL,
    TRANSITION_CANCEL_PROCESSED_PAYMENT,
    TRANSITION_CUSTOMER_CANCEL_PROCESSED_PAYMENT,
    TRANSITION_CANCEL_PAYED_PAYMENT,
    TRANSITION_CUSTOMER_CANCEL_PAYED_PAYMENT,
  ];

  // Bound the result set by creation date (transactions.query has no booking-date filter).
  // Default look-back 18 months; bookings are typically created within ~a year of travel.
  // ISO string, matching the working pattern in ManageListingsPage.duck.js (marketplace SDK).
  const lookbackMonths = Number(process.env.REACT_APP_CALENDAR_LOOKBACK_MONTHS) || 18;
  const createdAtStart = moment().utc().subtract(lookbackMonths, 'months').startOf('day').toISOString();

  const apiQueryParams = {
    lastTransitions: allTransitions,
    createdAtStart,
    include: ['booking', 'customer', 'listing'],
    'fields.transaction': ['lastTransition'],
    'fields.booking': ['start', 'end', 'state'],
    'fields.listing': ['title', 'description'],
    'fields.customer': ['profile.displayName'],
  };

  dispatch(fetchTransactionRequest());

  const onlyFilter = ['order', 'sale'];
  try {
    for (const filter of onlyFilter) {
      const meta = {};
      do {
        const response = await sdk.transactions.query({
          ...apiQueryParams,
          only: filter,
          page: meta.page || 1,
        });
        await dispatch(addMarketplaceEntities(response));
        meta.page = response.data.meta.page + 1;
        meta.totalPages = response.data.meta.totalPages;
      } while (meta.page <= meta.totalPages);
    }
    dispatch(fetchTransactionSuccess());
  } catch (e) {
    logError(e, 'fetch-transaction-for-month');
    dispatch(fetchTransactionError(storableError(e)));
  }
};

export const loadData = () => (dispatch) => {
  return Promise.all([dispatch(fetchTransactionForMonth())]);
};
